import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLang } from "@/context/LanguageContext";
import { X, ChevronLeft, ChevronRight, Timer, Pause, Play, RotateCcw, Mic, MicOff, ListChecks, Sun, PartyPopper } from "lucide-react";

const VOICE = {
  next: /\b(suivant|next|weiter|avanti|prochaine)\b/i,
  prev: /\b(précédent|precedent|retour|previous|back|zurück|zurueck|indietro)\b/i,
  timer: /\b(minuteur|timer|chrono|lancer|start)\b/i,
};
const LANG_CODE = { fr: "fr-FR", en: "en-US", de: "de-CH", it: "it-CH" };

// "10 min", "25 minutes", "1 h", "30 secondes" → seconds
export function detectTimer(text) {
  const h = text.match(/(\d+)\s*(h|heures?|hours?|stunden?|ore|ora)\b/i);
  const m = text.match(/(\d+)(?:\s*(?:à|-|–|to|bis|a)\s*(\d+))?\s*(min(?:utes?|uti|\.)?|minuten)\b/i);
  const s = text.match(/(\d+)\s*(sec(?:ondes?|onds?|\.)?|sekunden|secondi)\b/i);
  const total = (h ? +h[1] * 3600 : 0) + (m ? +(m[2] || m[1]) * 60 : 0) + (s ? +s[1] : 0);
  return total > 0 ? total : null;
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25, 0.5].forEach((at) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.15;
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.18);
    });
  } catch (error) { console.error("beep failed", error); }
  navigator.vibrate?.([300, 150, 300, 150, 600]);
}

function StepTimer({ seconds, onDone }) {
  const { t } = useLang();
  const [left, setLeft] = useState(seconds);
  const [running, setRunning] = useState(false);
  useEffect(() => { setLeft(seconds); setRunning(false); }, [seconds]);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setLeft((l) => {
      if (l <= 1) { clearInterval(id); setRunning(false); onDone(); return 0; }
      return l - 1;
    }), 1000);
    return () => clearInterval(id);
  }, [running, onDone]);
  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");
  const started = left !== seconds || running;
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-chenia-rosesoft/40 px-4 py-3" data-testid="step-timer">
      <Timer className="h-5 w-5 text-primary shrink-0" />
      {!started ? (
        <Button onClick={() => setRunning(true)} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="timer-start">{t("start_timer", { m: Math.round(seconds / 60) || 1 })}</Button>
      ) : (
        <>
          <span className={`font-serif text-4xl font-semibold tabular-nums ${left === 0 ? "text-primary" : ""}`} data-testid="timer-display">{left === 0 ? t("timer_done") : `${mm}:${ss}`}</span>
          {left > 0 && <Button variant="outline" size="icon" onClick={() => setRunning(!running)} className="rounded-full" data-testid="timer-toggle">{running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Button>}
          <Button variant="ghost" size="icon" onClick={() => { setRunning(false); setLeft(seconds); }} className="rounded-full" data-testid="timer-reset"><RotateCcw className="h-4 w-4" /></Button>
        </>
      )}
    </div>
  );
}

// Full-screen hands-free cooking: one step per screen, big text, timers, wake lock, keyboard + voice navigation
export function CookingMode({ recipe, steps, portions, fmtQty, onClose }) {
  const { t, tv, lang } = useLang();
  const [i, setI] = useState(0);
  const [showIngs, setShowIngs] = useState(false);
  const [voice, setVoice] = useState(false);
  const recRef = useRef(null);
  const total = steps.length;
  const done = i >= total;
  const timerSeconds = useMemo(() => (done ? null : detectTimer(steps[i])), [steps, i, done]);

  const next = useCallback(() => setI((x) => Math.min(total, x + 1)), [total]);
  const prev = useCallback(() => setI((x) => Math.max(0, x - 1)), []);
  const timerDone = useCallback(() => { beep(); toast.success(t("timer_done")); }, [t]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); if (e.key === "Escape" && !showIngs) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose, showIngs]);

  useEffect(() => {
    let lock;
    navigator.wakeLock?.request("screen").then((l) => { lock = l; }).catch((error) => console.error("wakeLock", error));
    document.body.style.overflow = "hidden";
    return () => { lock?.release?.(); document.body.style.overflow = ""; };
  }, []);

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  const toggleVoice = () => {
    if (!SR) { toast(t("voice_unsupported")); return; }
    if (voice) { recRef.current?.stop(); recRef.current = null; setVoice(false); toast(t("voice_off")); return; }
    const rec = new SR();
    rec.lang = LANG_CODE[lang] || "fr-FR"; rec.continuous = true; rec.interimResults = false;
    rec.onresult = (e) => {
      const said = e.results[e.results.length - 1][0].transcript;
      if (VOICE.next.test(said)) next(); else if (VOICE.prev.test(said)) prev();
      else if (VOICE.timer.test(said)) document.querySelector('[data-testid="timer-start"]')?.click();
    };
    rec.onend = () => { if (recRef.current === rec) { try { rec.start(); } catch (error) { console.error("voice restart", error); } } };
    rec.onerror = (e) => { if (e.error === "not-allowed") { setVoice(false); recRef.current = null; toast(t("voice_unsupported")); } };
    try { rec.start(); recRef.current = rec; setVoice(true); toast(t("voice_on")); } catch (error) { console.error(error); toast(t("voice_unsupported")); }
  };
  useEffect(() => () => { recRef.current?.stop(); recRef.current = null; }, []);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="cooking-mode" role="dialog" aria-modal>
      <header className="flex items-center justify-between gap-2 px-4 sm:px-8 h-16 border-b border-border">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold truncate">{tv(recipe.name)}</p>
          <p className="font-semibold" data-testid="cooking-progress">{done ? t("bon_appetit") : t("step_of", { n: i + 1, total })}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground mr-2"><Sun className="h-3 w-3" /> {t("screen_awake")} · {t("keyboard_hint")}</span>
          <Button variant="outline" size="icon" onClick={() => setShowIngs(true)} className="rounded-full" title={t("ingredients")} data-testid="cooking-ingredients"><ListChecks className="h-4 w-4" /></Button>
          <Button variant={voice ? "default" : "outline"} size="icon" onClick={toggleVoice} className={`rounded-full ${voice ? "bg-primary animate-pulse" : ""}`} title={t("voice_on")} data-testid="cooking-voice">{voice ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</Button>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full" aria-label={t("close")} data-testid="cooking-close"><X className="h-5 w-5" /></Button>
        </div>
      </header>

      <div className="h-1 bg-secondary"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${(Math.min(i, total) / total) * 100}%` }} /></div>

      <main className="flex-1 min-h-0 overflow-y-auto px-6 sm:px-12 py-8 flex flex-col max-w-4xl w-full mx-auto">
        {done ? (
          <div className="my-auto text-center chenia-fade-up" data-testid="cooking-done">
            <PartyPopper className="h-14 w-14 text-primary mx-auto" />
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold">{t("bon_appetit")}</h2>
            <p className="mt-3 text-muted-foreground">{t("cooking_done_sub")}</p>
          </div>
        ) : (
          <div key={i} className="my-auto chenia-fade-up">
            <span className="grid place-items-center h-14 w-14 rounded-full bg-primary text-primary-foreground font-serif text-2xl font-semibold">{i + 1}</span>
            <p className="mt-6 text-2xl sm:text-4xl leading-snug sm:leading-snug font-medium" data-testid="cooking-step-text">{steps[i]}</p>
            {timerSeconds && <div className="mt-8 max-w-md"><StepTimer seconds={timerSeconds} onDone={timerDone} /></div>}
          </div>
        )}
      </main>

      <footer className="px-4 sm:px-8 py-4 border-t border-border flex items-center gap-3">
        <Button variant="outline" onClick={prev} disabled={i === 0} className="rounded-full h-14 flex-1 text-base" data-testid="cooking-prev"><ChevronLeft className="h-5 w-5 mr-1" /> {t("prev_step")}</Button>
        {done ? (
          <Button onClick={onClose} className="rounded-full h-14 flex-1 text-base bg-primary hover:bg-chenia-terracottadark" data-testid="cooking-finish">{t("close")}</Button>
        ) : (
          <Button onClick={next} className="rounded-full h-14 flex-1 text-base bg-primary hover:bg-chenia-terracottadark" data-testid="cooking-next">{i === total - 1 ? t("finish_cooking") : t("next_step")} <ChevronRight className="h-5 w-5 ml-1" /></Button>
        )}
      </footer>

      <Sheet open={showIngs} onOpenChange={setShowIngs}>
        <SheetContent side="right" className="w-[88vw] sm:max-w-md overflow-y-auto" data-testid="cooking-ingredients-sheet">
          <SheetHeader><SheetTitle className="font-serif text-2xl">{t("ingredients")} · {portions} {portions > 1 ? t("portions") : t("portion")}</SheetTitle></SheetHeader>
          <ul className="mt-4 space-y-2.5">
            {recipe.ingredients.map((ing) => (
              <li key={ing.key} className="flex items-center justify-between gap-2 py-1 border-b border-border/50 last:border-0 text-base">
                <span>{tv(ing.name)}</span><span className="text-muted-foreground">{fmtQty(ing.qty)} {ing.unit}</span>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
    </div>,
    document.body,
  );
}
